import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { colorToRgba } from '../../../util/UtilFunctions';
import { Bar } from 'react-chartjs-2';
import { setAlarm } from '../../../redux/actions/general';
import { CsvExportFromApiButton, HorizontalSeparator, Spinner } from '../../common';
import { CardTitle } from 'reactstrap';
import { PropTypes } from 'prop-types';

import DefaultTemplate from '../../zoom/templates/DefaultTemplate';
import { WIDGET_SETTINGS } from '../../zoom/widgetSettings/LiveCaptionsView';
import ZoomHint from '../../zoom/ZoomHint';
import TrafficHint from '../../common/Hints/TrafficHint';
import { barChartZoomOptions } from '../../../constants/chartjs';

import {
   CenteredMsgShowing,
   ChartWrapper,
   ColoredText,
   HeightSetContainer,
   RightSide,
   SetChartHeight,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   TwoSideRow,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../styledComponents/styledComponents';

import { getHttpHeader } from '../../../util/FetchTools';
import { generateStackedBarChartData } from '../../../util/ChartJsProcessing';
import {
   hashSelector,
   startTimespanSelector,
   endTimespanSelector,
   titleTimespanSelector,
   intervalTimespanSelector,
   cancelTokenSelector,
   timespanSelector,
   tagsSelector,
   streamNamesSelector,
} from '../../../redux/selectors/selectors';
import useDataFetch from '../../../hooks/useDataFetch';

import ErrorDisplay from '../misuse/ErrorDisplay';
import { DefaultGrey } from '../../../constants/colors';

const barChartOptions = barChartZoomOptions();

function createChartDataFactory(series, colors) {
   return function createChartData(labels = [], ...dataArrays) {
      return {
         labels,
         datasets: series.map(({ label, zoomLabel }, i) => ({
            label,
            zoomLabel: zoomLabel ?? label,
            backgroundColor: colorToRgba(colors[i], 10),
            borderColor: colors[i],
            borderWidth: 1,
            data: dataArrays[i] ?? [],
         })),
      };
   };
}

function addZoomClickHandler(chartOptions, barRef, setModal) {
   return {
      ...chartOptions,
      onClick: (event, item) => {
         if (item.length === 0) {
            return;
         }
         const activePoint = barRef.current.chartInstance.getElementAtEvent(event)[0];
         const datasetIndex = activePoint._datasetIndex;
         const data = activePoint._chart.data;
         const index = item[0]._index;

         setModal({
            chosenIndex: index,
            toggleShow: true,
            selectedStackSubBar: data.datasets[datasetIndex].zoomLabel,
         });
      },
   };
}

/**
 * Config shape:
 * {
 *   metric: string,           — zoom metric constant
 *   title: string,            — card title
 *   csvFilename: string,
 *   tooltipText: string,      — HTML string for info tooltip
 *   getUrl: (start, end, interval, { tags, streamNames }) => string,
 *   filterUnsupportedMessage: string|null,  — overlay text shown in place of the chart when filters are unsupported
 *   filterUnsupportedCondition: (({ tags, streamNames }) => boolean)|null,  — optional; when it returns true the chart is replaced by the overlay
 *   series: Array<{ key: string, label: string, cssVar: string }>,
 *   unitName: string,         — passed to generateStackedBarChartData
 *   processingFn: fn|null,    — value transform, null means identity
 *   formatTotal: (value, isLoadingOrError) => string|number,
 *   guardOn204: boolean,      — skip processing on HTTP 204
 *   labelWidthPx: number,
 *   rightPanelMinWidthPx: number,
 *   spinnerTopMarginPx: number,
 * }
 */
function LiveCaptionsStackedBar({ height, config }) {
   const {
      metric,
      title,
      csvFilename,
      tooltipText,
      getUrl,
      filterUnsupportedMessage,
      filterUnsupportedCondition,
      series,
      unitName,
      processingFn,
      formatTotal,
      guardOn204,
      labelWidthPx,
      rightPanelMinWidthPx,
      spinnerTopMarginPx,
   } = config;

   const { createChartData, colors, stackedPropNames } = useMemo(() => {
      const resolvedColors = series.map(({ cssVar }) => getStyle(cssVar));
      return {
         createChartData: createChartDataFactory(series, resolvedColors),
         colors: resolvedColors,
         stackedPropNames: series.map(({ key }) => key),
      };
      // `series` is a module-level constant — omitted intentionally to avoid re-running on every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const [totals, setTotals] = useState(() => series.map(() => 0));
   const [grandTotal, setGrandTotal] = useState(0);
   const [chart, setChart] = useState(createChartData());
   const [noData, setNoData] = useState(false);

   const hash = useSelector(hashSelector);
   const start = useSelector(startTimespanSelector);
   const end = useSelector(endTimespanSelector);
   const timespanObj = useSelector(timespanSelector);
   const interval = useSelector(intervalTimespanSelector);
   const timespanTitle = useSelector(titleTimespanSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const tags = useSelector(tagsSelector);
   const streamNames = useSelector(streamNamesSelector);

   const dispatch = useDispatch();

   const [modal, setModal] = useState({
      toggleShow: false,
      chosenIndex: 0,
      selectedStackSubBar: WIDGET_SETTINGS[metric].categoryLabels[0],
   });

   const [timespansPerIndex, setTimespansPerIndex] = useState([{ start: moment.utc(), end: moment.utc() }]);

   const barRef = useRef(null);
   const chartOptions = addZoomClickHandler(barChartOptions, barRef, setModal);

   const header = useMemo(() => getHttpHeader(hash), [hash]);
   const url = getUrl(start, end, interval, { tags, streamNames });

   const { successful, isLoading, errorMessage, data, httpStatus } = useDataFetch(url, { header, cancelToken });

   useEffect(() => {
      if (successful) {
         if (guardOn204 && httpStatus === 204) {
            return;
         }
         const usage = data.data;
         setNoData(data.statusCode === 2000);

         const {
            datasetsDatas,
            labels,
            totals: newTotals,
            rawTotals,
            timespansPerIndex: newTimespans,
         } = generateStackedBarChartData(usage, timespanObj, stackedPropNames, unitName, processingFn ?? undefined);

         setTimespansPerIndex(newTimespans);
         setChart(createChartData(labels, ...datasetsDatas));
         setTotals(newTotals);
         const rawGrandTotal = rawTotals.reduce((sum, raw) => sum + raw, 0);
         setGrandTotal(processingFn ? processingFn(rawGrandTotal) : rawGrandTotal);
      }
      if (errorMessage) {
         dispatch(setAlarm('danger', errorMessage));
      }
   }, [
      data,
      successful,
      errorMessage,
      httpStatus,
      dispatch,
      timespanObj,
      guardOn204,
      unitName,
      processingFn,
      createChartData,
      stackedPropNames,
   ]);

   const filterUnsupported = filterUnsupportedCondition ? filterUnsupportedCondition({ tags, streamNames }) : false;
   const showOverlay = errorMessage || noData || filterUnsupported;
   const { toggleShow, chosenIndex, selectedStackSubBar } = modal;
   const chosenTimespan = timespansPerIndex[chosenIndex];

   return (
      <WidgetContainer>
         <WidgetHeaderContainer>
            <HeightSetContainer $heightInPx={23}>
               <TwoSideFlexContainer>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <CsvExportFromApiButton
                           url={url}
                           header={header}
                           cancelToken={cancelToken}
                           filename={csvFilename}
                        />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <TrafficHint sizePx={16} customTip={tooltipText} />
                     </SimpleFlexItem>
                     <HorizontalSeparator
                        color={DefaultGrey(1, 180)}
                        widthInPx={1}
                        heightInPx={18}
                        marginX_InPx={12}
                        marginTopInPx={0}
                     />
                     <SimpleFlexItem>
                        <ZoomHint />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
               </TwoSideFlexContainer>
            </HeightSetContainer>
         </WidgetHeaderContainer>
         <WidgetBodyContainer>
            <TwoSideRow marginRightPx={15}>
               <div>
                  <CardTitle className='mb-0'>{title}</CardTitle>
                  <div className='small text-muted'>{`${timespanTitle}\u00A0\u00A0\u00A0( UTC time )`}</div>
               </div>
               <RightSide minWidthPx={rightPanelMinWidthPx}>
                  {series.map(({ label }, i) => (
                     <TwoSideRow key={label} setNoWidth={true} marginLeftPx={15}>
                        <RightSide minWidthPx={labelWidthPx}>{label}:</RightSide>
                        <ColoredText color={colors[i]}>
                           {formatTotal(totals[i], isLoading || showOverlay)}
                        </ColoredText>
                     </TwoSideRow>
                  ))}
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={labelWidthPx}>Total:</RightSide>
                     <div>{formatTotal(grandTotal, isLoading || showOverlay)}</div>
                  </TwoSideRow>
               </RightSide>
            </TwoSideRow>
            <ChartWrapper className='chart-wrapper' marginTopPx={10} height={height}>
               <Spinner loading={isLoading} parentTopMarginPx={spinnerTopMarginPx}>
                  {showOverlay ? (
                     <CenteredMsgShowing height={height}>
                        {filterUnsupported ? (
                           <ErrorDisplay error={{ message: filterUnsupportedMessage }} msgColor={'rgb(80,80,80)'} />
                        ) : errorMessage ? (
                           <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'} />
                        ) : (
                           'No data available'
                        )}
                     </CenteredMsgShowing>
                  ) : (
                     <SetChartHeight height={height}>
                        <Bar ref={barRef} data={chart} options={chartOptions} height={height} />
                     </SetChartHeight>
                  )}
               </Spinner>
            </ChartWrapper>
         </WidgetBodyContainer>
         {toggleShow && (
            <DefaultTemplate
               timespan={chosenTimespan}
               metric={metric}
               term={selectedStackSubBar}
               settings={{
                  ...WIDGET_SETTINGS,
                  [metric]: {
                     ...WIDGET_SETTINGS[metric],
                     initialCategoryIdx: WIDGET_SETTINGS[metric].categoryLabels.indexOf(selectedStackSubBar),
                  },
               }}
               closeModal={() => setModal({ ...modal, toggleShow: false })}
            />
         )}
      </WidgetContainer>
   );
}

LiveCaptionsStackedBar.propTypes = {
   height: PropTypes.number,
   config: PropTypes.shape({
      metric: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      csvFilename: PropTypes.string.isRequired,
      tooltipText: PropTypes.string.isRequired,
      getUrl: PropTypes.func.isRequired,
      filterUnsupportedMessage: PropTypes.string,
      filterUnsupportedCondition: PropTypes.func,
      series: PropTypes.arrayOf(
         PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            cssVar: PropTypes.string.isRequired,
            zoomLabel: PropTypes.string,
         }),
      ).isRequired,
      unitName: PropTypes.string,
      processingFn: PropTypes.func,
      formatTotal: PropTypes.func.isRequired,
      guardOn204: PropTypes.bool,
      labelWidthPx: PropTypes.number.isRequired,
      rightPanelMinWidthPx: PropTypes.number.isRequired,
      spinnerTopMarginPx: PropTypes.number.isRequired,
   }).isRequired,
};

export default LiveCaptionsStackedBar;
